import {
  AlertTriangle,
  Banknote,
  FileWarning,
  TrendingDown,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { DashboardCharts, DashboardKpis } from '../../lib/dashboardApi';
import { formatCurrency } from '../../lib/format';
import { CountUp, FIRMIO_COLORS, Sparkline } from './charts';

interface KpiCardsProps {
  kpis: DashboardKpis;
  charts?: DashboardCharts;
}

interface Trend {
  pct: number;
  /** Whether an upward move is good (revenue) or bad (expenses). */
  upIsGood: boolean;
}

export interface Kpi {
  label: string;
  /** Numeric value for the count-up animation. */
  numeric: number;
  format: (n: number) => string;
  icon: LucideIcon;
  accent: string;
  detail?: string;
  detailAccent?: string;
  trend?: Trend;
  spark?: number[];
  sparkColor?: string;
}

/** The six KPI cards, addressable individually by the widget registry. */
export type KpiKind =
  | 'revenue'
  | 'expenses'
  | 'profit'
  | 'receivables'
  | 'payables'
  | 'urgentTasks';

export const KPI_KINDS: KpiKind[] = [
  'revenue',
  'expenses',
  'profit',
  'receivables',
  'payables',
  'urgentTasks',
];

function TrendArrow({ trend }: { trend: Trend }) {
  const up = trend.pct >= 0;
  const good = up === trend.upIsGood;
  const color = good ? 'text-emerald-400' : 'text-red-400';
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${color}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {Math.abs(Math.round(trend.pct))}%
    </span>
  );
}

/** Build the display config for one KPI card, keyed by kind (not label). */
export function buildKpiConfig(kind: KpiKind, kpis: DashboardKpis, charts?: DashboardCharts): Kpi {
  const { currency } = kpis;
  switch (kind) {
    case 'revenue':
      return {
        label: 'Venituri luna curentă',
        numeric: kpis.revenueThisMonth,
        format: (n) => formatCurrency(n, currency),
        icon: TrendingUp,
        accent: FIRMIO_COLORS.green,
        trend: { pct: kpis.revenueTrendPct, upIsGood: true },
        detail: 'vs luna trecută',
        spark: charts?.revenueSparkline,
        sparkColor: FIRMIO_COLORS.green,
      };
    case 'expenses':
      return {
        label: 'Cheltuieli luna curentă',
        numeric: kpis.expensesThisMonth,
        format: (n) => formatCurrency(n, currency),
        icon: TrendingDown,
        accent: FIRMIO_COLORS.red,
        trend: { pct: kpis.expensesTrendPct, upIsGood: false },
        detail: 'vs luna trecută',
        spark: charts?.expensesSparkline,
        sparkColor: FIRMIO_COLORS.red,
      };
    case 'profit':
      return {
        label: 'Profit net luna',
        numeric: kpis.profitThisMonth,
        format: (n) => formatCurrency(n, currency),
        icon: Banknote,
        accent: kpis.profitThisMonth >= 0 ? FIRMIO_COLORS.blue : FIRMIO_COLORS.red,
        detail: `marjă: ${Math.round(kpis.profitMargin)}%`,
        detailAccent: kpis.profitThisMonth >= 0 ? 'text-emerald-400' : 'text-red-400',
        spark: charts?.profitSparkline,
        sparkColor: kpis.profitThisMonth >= 0 ? FIRMIO_COLORS.blue : FIRMIO_COLORS.red,
      };
    case 'receivables':
      return {
        label: 'Facturi neîncasate',
        numeric: kpis.unpaidReceivables,
        format: (n) => formatCurrency(n, currency),
        icon: Wallet,
        accent: '#F59E0B',
        detail: `${kpis.unpaidReceivablesCount} ${kpis.unpaidReceivablesCount === 1 ? 'factură' : 'facturi'} restante`,
        detailAccent: 'text-amber-400',
      };
    case 'payables':
      return {
        label: 'Plăți de efectuat',
        numeric: kpis.payablesDue,
        format: (n) => formatCurrency(n, currency),
        icon: FileWarning,
        accent: FIRMIO_COLORS.red,
        detail: `${kpis.payablesDueCount} ${kpis.payablesDueCount === 1 ? 'factură scadentă' : 'facturi scadente'}`,
        detailAccent: 'text-red-400',
      };
    case 'urgentTasks':
      return {
        label: 'Task-uri urgente',
        numeric: kpis.urgentTaskCount,
        format: (n) => String(Math.round(n)),
        icon: AlertTriangle,
        accent: kpis.urgentTaskCount > 0 ? FIRMIO_COLORS.red : FIRMIO_COLORS.blue,
        detail: `${kpis.overdueTaskCount} overdue + ${kpis.criticalTaskCount} critice`,
        detailAccent: kpis.urgentTaskCount > 0 ? 'text-red-400' : 'text-muted-foreground',
      };
  }
}

/**
 * A single KPI card: big animated number, trend arrow, relevant icon, subtle
 * gradient background and an optional 6-month sparkline.
 */
export function KpiCard({ card }: { card: Kpi }) {
  const Icon = card.icon;
  return (
    <div
      className="group relative h-full overflow-hidden rounded-2xl border border-border p-4 shadow-card transition duration-200 hover:-translate-y-0.5 hover:shadow-card-hover"
      style={{
        background: `linear-gradient(150deg, ${card.accent}14 0%, var(--card) 55%)`,
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="line-clamp-2 text-xs text-muted-foreground">{card.label}</p>
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: `${card.accent}1f`, color: card.accent }}
        >
          <Icon className="h-4 w-4" aria-hidden />
        </span>
      </div>

      <CountUp
        value={card.numeric}
        format={card.format}
        className="mt-2 block font-heading text-[1.65rem] font-bold leading-none tracking-tight text-foreground"
      />

      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {card.trend && <TrendArrow trend={card.trend} />}
          {card.detail && (
            <span className={`truncate text-xs ${card.detailAccent ?? 'text-muted-foreground'}`}>
              {card.detail}
            </span>
          )}
        </div>
        {card.spark && card.spark.length > 1 && (
          <Sparkline data={card.spark} color={card.sparkColor} width={64} height={24} />
        )}
      </div>
    </div>
  );
}

/**
 * Top-bar KPI cards (CLE-256 / redesign CLE-257). Big animated numbers, trend
 * arrows, a relevant icon, a subtle gradient background and a 6-month sparkline
 * per financial card for an at-a-glance COO view.
 */
export function KpiCards({ kpis, charts }: KpiCardsProps) {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      {KPI_KINDS.map((kind) => (
        <KpiCard key={kind} card={buildKpiConfig(kind, kpis, charts)} />
      ))}
    </div>
  );
}
